import React, { useEffect, useState } from 'react';
import { X, IdentificationCard, Camera, Check, ShieldCheck, ArrowRight, UserCircle, Clock, WarningCircle, XCircle, IdentificationBadge } from '@phosphor-icons/react';
import { Spinner } from '../ui/Spinner';
import { getSession, getVerificationStates, setVerificationState, type VerificationState } from '../../lib/utils';
interface VerifyIdentityModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}
type Step = 'intro' | 'id_type_select' | 'id_capture' | 'id_scanning' | 'id_data_review' | 'selfie' | 'verifying' | 'success' | 'pending' | 'rejected' | 'expired';
export function VerifyIdentityModal({
  open,
  onClose,
  onSuccess
}: VerifyIdentityModalProps) {
  const [step, setStep] = useState<Step>('intro');
  const [idType, setIdType] = useState<'ci' | 'passport'>('ci');
  useEffect(() => {
    if (open) {
      const session = getSession();
      if (session?.email) {
        const states = getVerificationStates(session.email);
        const idState = states.identity;
        if (idState?.status === 'pending') setStep('pending');else if (idState?.status === 'rejected') setStep('rejected');else if (idState?.status === 'expired') setStep('expired');else setStep('intro');
      } else {
        setStep('intro');
      }
    }
  }, [open]);
  // Auto-advance scanning + verifying
  useEffect(() => {
    if (step === 'id_scanning') {
      const t = setTimeout(() => setStep('id_data_review'), 2200);
      return () => clearTimeout(t);
    }
    if (step === 'verifying') {
      const t = setTimeout(() => {
        const session = getSession();
        if (session?.email) {
          setVerificationState(session.email, 'identity', {
            status: 'pending',
            submittedAt: new Date().toISOString()
          });
        }
        setStep('success');
      }, 1800);
      return () => clearTimeout(t);
    }
  }, [step]);
  // Escape
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && step !== 'id_scanning' && step !== 'verifying') {
        onClose();
      }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, step, onClose]);
  // Lock scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
  if (!open) return null;
  const canClose = step !== 'id_scanning' && step !== 'verifying';
  return <div role="dialog" aria-modal="true" aria-label="Verifică identitate" className="fixed inset-0 z-50 bg-dark-1/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 lg:pl-[16rem] animate-in fade-in duration-200" onClick={() => canClose && onClose()} data-id="element-1907">
      <div className="w-full sm:max-w-md bg-dark-2 sm:rounded-2xl rounded-t-2xl border-t sm:border border-dark-3 animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1908">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-dark-3 shrink-0" data-id="element-1909">
          <h2 className="text-base font-semibold text-light-1" data-id="element-1910">
            Verifică identitate
          </h2>
          <button type="button" onClick={onClose} disabled={!canClose} aria-label="Închide" className="w-9 h-9 inline-flex items-center justify-center rounded-md text-light-3 hover:text-light-1 hover:bg-dark-3 transition-colors cursor-pointer disabled:opacity-50" data-id="element-1911">
            <X size={18} weight="bold" data-id="element-1912" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-6 sm:py-8" data-id="element-1913">
          {step === 'intro' && <div data-id="element-1914">
              <div className="flex flex-col items-center text-center mb-6" data-id="element-1915">
                <div className="w-14 h-14 rounded-full bg-lime-5 border border-lime-4 flex items-center justify-center mb-3" data-id="element-1916">
                  <IdentificationCard size={26} weight="regular" className="text-lime-1" data-id="element-1917" />
                </div>
                <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-1918">
                  Confirmă-ți identitatea
                </h3>
                <p className="text-sm text-light-3 max-w-xs" data-id="element-1919">
                  Câteva secunde și obții badge-ul verde de profil verificat.
                </p>
              </div>

              <ol className="space-y-3 mb-6" data-id="element-1920">
                <li className="flex items-start gap-3 px-4 py-3 rounded-xl bg-dark-1 border border-dark-3" data-id="element-1921">
                  <div className="shrink-0 w-7 h-7 rounded-full bg-dark-3 flex items-center justify-center text-xs font-semibold text-light-2" data-id="element-1922">
                    1
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5" data-id="element-1923">
                    <p className="text-sm font-medium text-light-1" data-id="element-1924">
                      Fotografiază actul de identitate
                    </p>
                    <p className="text-xs text-light-3 mt-0.5" data-id="element-1925">
                      Buletin sau pașaport — clar, fără reflexii.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3 px-4 py-3 rounded-xl bg-dark-1 border border-dark-3" data-id="element-1926">
                  <div className="shrink-0 w-7 h-7 rounded-full bg-dark-3 flex items-center justify-center text-xs font-semibold text-light-2" data-id="element-1927">
                    2
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5" data-id="element-1928">
                    <p className="text-sm font-medium text-light-1" data-id="element-1929">
                      Fă un selfie scurt
                    </p>
                    <p className="text-xs text-light-3 mt-0.5" data-id="element-1930">
                      Pentru a verifica că tu ești cel din act.
                    </p>
                  </div>
                </li>
              </ol>

              <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-lime-5 border border-lime-4" data-id="element-1931">
                <ShieldCheck size={16} weight="fill" className="text-lime-1 shrink-0 mt-0.5" data-id="element-1932" />
                <p className="text-xs text-light-2 leading-relaxed" data-id="element-1933">
                  Datele tale sunt criptate și folosite doar pentru verificare.
                  Nu apar pe profilul public.
                </p>
              </div>
            </div>}

          {step === 'id_type_select' && <div className="flex flex-col items-center text-center" data-id="element-1934">
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-1935">
                Alege tipul de document
              </h3>
              <p className="text-sm text-light-3 mb-6 max-w-xs" data-id="element-1936">
                Ce document dorești să folosești pentru verificare?
              </p>

              <div className="w-full space-y-3" data-id="element-1937">
                <button type="button" onClick={() => setIdType('ci')} className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-colors cursor-pointer ${idType === 'ci' ? 'bg-lime-5 border-lime-4' : 'bg-dark-1 border-dark-3 hover:border-light-4'}`} data-id="element-1938">
                  <IdentificationCard size={24} className={idType === 'ci' ? 'text-lime-1' : 'text-light-3'} data-id="element-1939" />
                  <div className="text-left" data-id="element-1940">
                    <p className={`text-sm font-medium ${idType === 'ci' ? 'text-light-1' : 'text-light-2'}`} data-id="element-1941">
                      Carte de identitate (Buletin)
                    </p>
                  </div>
                </button>
                <button type="button" onClick={() => setIdType('passport')} className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-colors cursor-pointer ${idType === 'passport' ? 'bg-lime-5 border-lime-4' : 'bg-dark-1 border-dark-3 hover:border-light-4'}`} data-id="element-1942">
                  <IdentificationBadge size={24} className={idType === 'passport' ? 'text-lime-1' : 'text-light-3'} data-id="element-1943" />
                  <div className="text-left" data-id="element-1944">
                    <p className={`text-sm font-medium ${idType === 'passport' ? 'text-light-1' : 'text-light-2'}`} data-id="element-1945">
                      Pașaport
                    </p>
                  </div>
                </button>
              </div>
            </div>}

          {step === 'id_capture' && <div className="flex flex-col items-center text-center" data-id="element-1946">
              <p className="text-xs font-medium text-light-3 uppercase tracking-wider mb-3" data-id="element-1947">
                Pasul 1 din 2
              </p>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-1948">
                Fotografiază {idType === 'ci' ? 'buletinul' : 'pașaportul'}
              </h3>
              <p className="text-sm text-light-3 mb-5 max-w-xs" data-id="element-1949">
                Așază actul în chenar și apasă pe buton.
              </p>

              {/* Mock camera viewport */}
              <div className="relative w-full aspect-[4/3] rounded-xl bg-dark-1 border border-dark-3 overflow-hidden" data-id="element-1950">
                <div className="absolute inset-4 border-2 border-dashed border-lime-4 rounded-lg flex items-center justify-center" data-id="element-1951">
                  {idType === 'ci' ? <IdentificationCard size={64} weight="duotone" className="text-light-4" data-id="element-1952" /> : <IdentificationBadge size={64} weight="duotone" className="text-light-4" data-id="element-1953" />}
                </div>
                {/* Corner brackets */}
                <span className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-lime-1 rounded-tl" data-id="element-1954" />
                <span className="absolute top-3 right-3 w-5 h-5 border-t-2 border-r-2 border-lime-1 rounded-tr" data-id="element-1955" />
                <span className="absolute bottom-3 left-3 w-5 h-5 border-b-2 border-l-2 border-lime-1 rounded-bl" data-id="element-1956" />
                <span className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-lime-1 rounded-br" data-id="element-1957" />
              </div>
            </div>}

          {step === 'id_scanning' && <div className="flex flex-col items-center text-center" data-id="element-1958">
              <p className="text-xs font-medium text-light-3 uppercase tracking-wider mb-3" data-id="element-1959">
                Pasul 1 din 2
              </p>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-1960">
                Se scanează documentul
              </h3>
              <p className="text-sm text-light-3 mb-5" data-id="element-1961">
                Nu mișca dispozitivul.
              </p>

              <div className="relative w-full aspect-[4/3] rounded-xl bg-dark-1 border border-dark-3 overflow-hidden" data-id="element-1962">
                <div className="absolute inset-4 border-2 border-lime-1 rounded-lg flex items-center justify-center" data-id="element-1963">
                  {idType === 'ci' ? <IdentificationCard size={64} weight="duotone" className="text-light-4" data-id="element-1964" /> : <IdentificationBadge size={64} weight="duotone" className="text-light-4" data-id="element-1965" />}
                </div>
                <div className="absolute inset-x-4 top-4 bottom-4 overflow-hidden pointer-events-none" data-id="element-1966">
                  <div className="mp-scan-line absolute inset-x-0 h-0.5 bg-lime-1" data-id="element-1967" />
                </div>
              </div>
              <style data-id="element-1968">{`@keyframes mp-id-scan{0%{top:0%}50%{top:100%}100%{top:0%}}.mp-scan-line{animation:mp-id-scan 2s ease-in-out infinite;box-shadow:0 0 12px rgba(205,245,61,0.7)}`}</style>
            </div>}

          {step === 'id_data_review' && <div className="flex flex-col items-center text-center" data-id="element-1969">
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-1970">
                Verifică datele extrase
              </h3>
              <p className="text-sm text-light-3 mb-6 max-w-xs" data-id="element-1971">
                Asigură-te că datele de mai jos sunt corecte.
              </p>

              <div className="w-full bg-dark-1 border border-dark-3 rounded-xl p-4 space-y-3 text-left" data-id="element-1972">
                <div data-id="element-1973">
                  <p className="text-[10px] uppercase tracking-wider text-light-4 font-medium mb-0.5" data-id="element-1974">
                    Nume complet
                  </p>
                  <p className="text-sm font-medium text-light-1" data-id="element-1975">
                    {getSession()?.name || 'Utilizator Demo'}
                  </p>
                </div>
                <div data-id="element-1976">
                  <p className="text-[10px] uppercase tracking-wider text-light-4 font-medium mb-0.5" data-id="element-1977">
                    Tip document
                  </p>
                  <p className="text-sm font-medium text-light-1" data-id="element-1978">
                    {idType === 'ci' ? 'Carte de identitate' : 'Pașaport'}
                  </p>
                </div>
                <div data-id="element-1979">
                  <p className="text-[10px] uppercase tracking-wider text-light-4 font-medium mb-0.5" data-id="element-1980">
                    Valabil până la
                  </p>
                  <p className="text-sm font-medium text-light-1" data-id="element-1981">
                    14 Mai 2031
                  </p>
                </div>
              </div>
            </div>}

          {step === 'selfie' && <div className="flex flex-col items-center text-center" data-id="element-1982">
              <p className="text-xs font-medium text-light-3 uppercase tracking-wider mb-3" data-id="element-1983">
                Pasul 2 din 2
              </p>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-1984">
                Fă un selfie
              </h3>
              <p className="text-sm text-light-3 mb-5 max-w-xs" data-id="element-1985">
                Privește camera și păstrează o expresie neutră.
              </p>

              <div className="relative w-full aspect-square max-w-[260px] rounded-full bg-dark-1 border-2 border-dashed border-lime-4 overflow-hidden flex items-center justify-center" data-id="element-1986">
                <UserCircle size={120} weight="duotone" className="text-light-4" data-id="element-1987" />
              </div>
            </div>}

          {step === 'verifying' && <div className="flex flex-col items-center text-center py-8" data-id="element-1988">
              <div className="w-14 h-14 rounded-full bg-dark-1 border border-dark-3 flex items-center justify-center mb-4" data-id="element-1989">
                <Spinner size="md" data-id="element-1990" />
              </div>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-1991">
                Verificăm identitatea
              </h3>
              <p className="text-sm text-light-3" data-id="element-1992">
                Durează doar câteva secunde.
              </p>
            </div>}

          {step === 'success' && <div className="flex flex-col items-center text-center py-4" data-id="element-1993">
              <div className="w-16 h-16 rounded-full bg-lime-5 border border-lime-4 flex items-center justify-center mb-4" data-id="element-1994">
                <Check size={32} weight="bold" className="text-lime-1" data-id="element-1995" />
              </div>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-1996">
                Documente trimise
              </h3>
              <p className="text-sm text-light-3 max-w-xs mb-4" data-id="element-1997">
                Echipa noastră va verifica documentele în maxim 24h. Vei primi o
                notificare când badge-ul apare pe profil.
              </p>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-dark-3 border border-dark-4 text-xs font-medium text-light-2" data-id="element-1998">
                <Spinner size="sm" data-id="element-1999" />
                În verificare
              </div>
            </div>}

          {step === 'pending' && <div className="flex flex-col items-center text-center py-4" data-id="element-2000">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4" data-id="element-2001">
                <Clock size={32} weight="bold" className="text-amber-400" data-id="element-2002" />
              </div>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-2003">
                Identitate în verificare
              </h3>
              <p className="text-sm text-light-3 max-w-xs" data-id="element-2004">
                Documentele tale au fost trimise și sunt în curs de verificare.
                Acest proces durează de obicei sub 24h.
              </p>
            </div>}

          {step === 'rejected' && <div className="flex flex-col items-center text-center py-4" data-id="element-2005">
              <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-4" data-id="element-2006">
                <WarningCircle size={32} weight="bold" className="text-red-400" data-id="element-2007" />
              </div>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-2008">
                Verificare respinsă
              </h3>
              <p className="text-sm text-light-3 max-w-xs mb-4" data-id="element-2009">
                Din păcate, nu am putut valida documentul tău. Motivul: imaginea
                este neclară sau conține reflexii.
              </p>
            </div>}

          {step === 'expired' && <div className="flex flex-col items-center text-center py-4" data-id="element-2010">
              <div className="w-16 h-16 rounded-full bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mb-4" data-id="element-2011">
                <XCircle size={32} weight="bold" className="text-orange-400" data-id="element-2012" />
              </div>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-2013">
                Document expirat
              </h3>
              <p className="text-sm text-light-3 max-w-xs mb-4" data-id="element-2014">
                Documentul de identitate folosit anterior a expirat. Te rugăm să
                încarci un document valabil.
              </p>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-dark-3 shrink-0" data-id="element-2015">
          {step === 'intro' && <button type="button" onClick={() => setStep('id_type_select')} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold text-sm transition-colors cursor-pointer" data-id="element-2016">
              Începe verificarea <ArrowRight size={16} weight="bold" data-id="element-2017" />
            </button>}
          {step === 'id_type_select' && <button type="button" onClick={() => setStep('id_capture')} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold text-sm transition-colors cursor-pointer" data-id="element-2018">
              Continuă
            </button>}
          {step === 'id_capture' && <button type="button" onClick={() => setStep('id_scanning')} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold text-sm transition-colors cursor-pointer" data-id="element-2019">
              <Camera size={18} weight="fill" data-id="element-2020" />
              Capturează documentul
            </button>}
          {step === 'id_scanning' && <button type="button" disabled className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-dark-3 text-light-4 font-semibold text-sm cursor-not-allowed" data-id="element-2021">
              <Spinner size="sm" data-id="element-2022" />
              Se procesează...
            </button>}
          {step === 'id_data_review' && <button type="button" onClick={() => setStep('selfie')} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold text-sm transition-colors cursor-pointer" data-id="element-2023">
              Confirmă datele
            </button>}
          {step === 'selfie' && <button type="button" onClick={() => setStep('verifying')} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold text-sm transition-colors cursor-pointer" data-id="element-2024">
              <Camera size={18} weight="fill" data-id="element-2025" />
              Fă selfie
            </button>}
          {step === 'verifying' && <button type="button" disabled className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-dark-3 text-light-4 font-semibold text-sm cursor-not-allowed" data-id="element-2026">
              <Spinner size="sm" data-id="element-2027" />
              Se verifică...
            </button>}
          {step === 'success' && <button type="button" onClick={onSuccess} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold text-sm transition-colors cursor-pointer" data-id="element-2028">
              Gata
            </button>}
          {step === 'pending' && <button type="button" onClick={onClose} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-dark-3 hover:bg-dark-4 text-light-1 font-semibold text-sm transition-colors cursor-pointer" data-id="element-2029">
              Închide
            </button>}
          {(step === 'rejected' || step === 'expired') && <button type="button" onClick={() => setStep('id_type_select')} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold text-sm transition-colors cursor-pointer" data-id="element-2030">
              Încearcă din nou
            </button>}
        </div>
      </div>
    </div>;
}